'use strict';

import React from "react";
import PropTypes from "prop-types";

import {FaEllipsisH} from "react-icons/fa";

import Dropdown from "react-bootstrap/Dropdown";

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import planItemStore from "@uw-it-sis/lib-react-myplan/lib/PlanItemStore";
import {makeUniqueId} from "@uw-it-sis/lib-react/lib/Utils";

import planItemModalStore from "../registration/PlanItemModalStore";
import {PlanItemType} from "../lib/Constants";

/**
 * Component for actions menu on a plan item.
 */
const PlanItemActions = ({type, title, data = {}, sectionsAvailable = false, placeholder = false, isBlock = false, isPublished, openForPlanning}) => {
    let disableAction = appStore.isAdviser();

    let changeType;
    // Add "backup" flag to data for passing to edit placeholder modal
    data.backup = false;
    switch (type) {
        case PlanItemType.planned:
            changeType = PlanItemType.backup;
            break;
        case PlanItemType.backup:
            data.backup = true;
            changeType = PlanItemType.planned;
            break;
    }

    let analyticsData = {context: "PlanView-Modal"}

    const onDeleteCourse = () => {
        planItemModalStore.openModal('delete', data, analyticsData);
    };

    const onMoveCourse = () => {
        planItemModalStore.openModal('move', data, analyticsData);
    };

    const onChangeType = () => {
        let code = placeholder ? data.placeHolderCode : data.courseDetails.code;
        planItemStore.changeType(data.planItemDataObject.id, changeType, code, data.note, data.placeHolderText, data.credit);
    };

    const onAddEditSections = () => {
        planItemModalStore.openModal('update', data, analyticsData);
    };

    const onAddEditNote = () => {
        planItemModalStore.openModal('updateNote', data, analyticsData);
    };

    const onEditPlaceholder = () => {
        planItemModalStore.openModal('updatePlaceholder', data, analyticsData);
    };

    const onViewCourseSummary = () => {
        planItemModalStore.openModal('view',data, analyticsData);
    };

    const onAcceptCourseRecommendation = () => {
        planItemModalStore.openModal('accept',data, analyticsData);
    };

    const onAcceptPlaceholderRecommendation = () => {
        planItemModalStore.openModal('acceptPlaceholder',data, analyticsData);
    };

    let divider = <Dropdown.Divider key="divider" />;
    let view;
    if ( ! placeholder ) {
        view = <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="view" onClick={onViewCourseSummary}>View Course Summary</Dropdown.Item>;
    }
    let addEditNote;
    if ( ! placeholder && openForPlanning ) {
        addEditNote = <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="addEditNote" disabled={disableAction} onClick={onAddEditNote}>Add / Edit Note</Dropdown.Item>;
    }
    let editPlaceHolder = null;
    if ( placeholder && openForPlanning ) {
        editPlaceHolder = <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="editPlaceHolder" disabled={disableAction} onClick={onEditPlaceholder}>Edit Placeholder / Note</Dropdown.Item>;
    }
    let select;
    if ( sectionsAvailable && ! placeholder && openForPlanning && isPublished ) {
        select = <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="select" disabled={disableAction} onClick={onAddEditSections}>Select Section(s)</Dropdown.Item>;
    }

    let move;
    // Don't allow move on items part of a block reg
    if ( ! isBlock ) {
        move =
            <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="move" disabled={disableAction} onClick={onMoveCourse}>
                Move to Another Quarter
            </Dropdown.Item>;
    }

    let remove =
        <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="remove" disabled={disableAction} onClick={onDeleteCourse}>
            Delete
        </Dropdown.Item>;

    /**
     * The ability to delete a recommendation. Only an adviser can do so
     */
    let removeRecommendation =
        <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="removeRecommendation" onClick={onDeleteCourse}>
            Delete
        </Dropdown.Item>;

    let accept = null;
    if ( !placeholder) {
        accept = <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="accept" disabled={disableAction} onClick={onAcceptCourseRecommendation}>Add Recommendation</Dropdown.Item>;
    } else {
        accept = <Dropdown.Item role="menuitem" className="cursor-pointer" aria-haspopup={true} key="acceptPlaceholder" disabled={disableAction} onClick={onAcceptPlaceholderRecommendation}>Add Recommendation</Dropdown.Item>
    }

    let change;
    // Only allow moving from planned/backup if the term is open for planning and is not part of a block reg
    if ( openForPlanning && ! isBlock ) {
        change =
            <Dropdown.Item role="menuitem" className="cursor-pointer" key="change" disabled={disableAction} onClick={onChangeType}>
                Mark as <span className="text-capitalize">{changeType}</span>
            </Dropdown.Item>;
    }

    let dropdownMenu = [];
    switch (type) {
        case PlanItemType.recommended:
            if (appStore.isAdviser()){
                dropdownMenu = [view,removeRecommendation];
            } else {
                dropdownMenu = [accept];
            }
            break;
        case PlanItemType.planned:
        case PlanItemType.backup:
            dropdownMenu = [view, select, editPlaceHolder, addEditNote, (view || select || editPlaceHolder || addEditNote ? divider : null), change, move, remove];
            break;
    }

    if ( dropdownMenu.length === 0 ) {
        return null;
    }

    let a11yTitle = `More actions for ${title}`;

    let menuId = makeUniqueId();

    return (
        <Dropdown
            align="end" 
            drop="up" 
            focusFirstItemOnShow="keyboard">
            <Dropdown.Toggle id={`menu-toggle-${menuId}`}
                             variant="icon"
                             className="dropdown-no-caret text-muted"
                             title={a11yTitle}
                             aria-label={a11yTitle}
                             aria-controls={`menu-${menuId}`}>
                <FaEllipsisH aria-hidden="true" className="fs-5"/>
            </Dropdown.Toggle>
            <Dropdown.Menu id={`menu-${menuId}`}
                           renderOnMount={true}
                           className="shadow-sm"
                           role="menu"
                           aria-labelledby={`menu-toggle-${menuId}`}>
                {dropdownMenu}
            </Dropdown.Menu>
        </Dropdown>
    );
}

PlanItemActions.propTypes = {
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(Object.keys(PlanItemType)).isRequired,
    sectionsAvailable: PropTypes.bool.isRequired,
    data: PropTypes.object.isRequired,
    placeholder: PropTypes.bool,
    isBlock: PropTypes.bool,
    openForPlanning: PropTypes.bool,
    isPublished: PropTypes.bool
};

export default PlanItemActions;